// MailInfoRow.tsx
import React from "react";
import {
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { MailInfo } from "IF/interfaceType";
import { mailColumnWidths } from "./SettingMail";
import { maskString } from "Utils/format";

interface MailInfoRowProps {
  row: MailInfo;
  index: number;
  isEditing: boolean;
  currentData: MailInfo;
  handleChange: (
    index: number,
    field: keyof MailInfo,
    value: string | boolean
  ) => void;
  handleCheckboxChange: (
    index: number,
    field: keyof MailInfo
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveRow: (index: number) => void;
  handleCancelRow: (index: number) => void;
  handleDelete: (index: number) => void;
  startEditingRow: (index: number) => void;
  openModal: (message: string, confirmAction: () => void) => void;
}

const SettingMailRow: React.FC<MailInfoRowProps> = ({
  row,
  index,
  isEditing,
  currentData,
  handleChange,
  handleCheckboxChange,
  handleSaveRow,
  handleCancelRow,
  handleDelete,
  startEditingRow,
  openModal,
}) => {
  const handleOpenModal = (id: number) => {
    openModal("選択した配信情報を削除します。よろしいですか？", () =>
      handleDelete?.(id)
    );
  };

  return (
    <TableRow key={row.id || index}>
      <TableCell
        sx={{ minWidth: mailColumnWidths.mailName, whiteSpace: "nowrap" }}
      >
        <Typography>{row.mailName}</Typography>
      </TableCell>

      <TableCell>
        <Typography>{row.mailAddress}</Typography>
      </TableCell>

      <TableCell>
        {isEditing ? (
          <Checkbox
            checked={!!currentData.alertCheckbox}
            onChange={handleCheckboxChange(index, "alertCheckbox")}
            disabled={!row.mailAddress}
            aria-label={`警報チェックボックス-${index}`}
          />
        ) : (
          <Checkbox
            checked={!!row.alertCheckbox}
            disabled
            aria-label={`警報チェックボックス-${index}`}
          />
        )}
      </TableCell>

      <TableCell>
        {isEditing ? (
          <Checkbox
            checked={!!currentData.connectCheckbox}
            onChange={handleCheckboxChange(index, "connectCheckbox")}
            disabled={!row.mailAddress}
            aria-label={`通信不可チェックボックス-${index}`}
          />
        ) : (
          <Checkbox
            checked={!!row.connectCheckbox}
            disabled
            aria-label={`通信不可チェックボックス-${index}`}
          />
        )}
      </TableCell>

      <TableCell>
        {isEditing ? (
          <Typography>{row.telNumber}</Typography>
        ) : (
          <Typography>{maskString(row.telNumber)}</Typography>
        )}
      </TableCell>

      <TableCell>
        {isEditing ? (
          <Checkbox
            checked={!!currentData?.smsCheckbox}
            disabled={!row.telNumber}
            onChange={handleCheckboxChange(index, "smsCheckbox")}
          />
        ) : (
          <Checkbox checked={!!row.smsCheckbox} disabled />
        )}
      </TableCell>

      <TableCell>
        {isEditing ? (
          <>
            <Tooltip title={"保存"} placement="top" arrow>
              <IconButton onClick={() => handleSaveRow(index)}>
                <SaveIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={"キャンセル"} placement="top" arrow>
              <IconButton onClick={() => handleCancelRow(index)}>
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title={"編集"} placement="top" arrow>
              <IconButton onClick={() => startEditingRow(index)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={"削除"} placement="top" arrow>
              <IconButton onClick={() => handleOpenModal(index)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

export default SettingMailRow;
